import { useCallback, useEffect, useState } from "react";
import { MonitorSmartphone, Plus, Search, FileDown, Pencil, Trash2, BadgeCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useDebounced } from "@/lib/useDebounced";
import { LoadMoreBar, PAGE_SIZE, totalFrom } from "@/components/LoadMoreBar";
import { IconAction } from "@/components/IconAction";
import { AssetDialog, CONDITIONS, STATUSES } from "@/components/platform/AssetDialog";

const STATUS_TONE = { in_stock: "bg-slate-100 text-slate-700", sold: "bg-emerald-100 text-emerald-800", leased: "bg-blue-100 text-blue-800" };
const COND_TONE = { new: "text-emerald-700", excellent: "text-teal-700", good: "text-amber-700", fair: "text-orange-700" };
const COUNT_TONE = {
  all: { base: "border-sky-200 bg-sky-50", on: "border-sky-400 ring-sky-300", label: "text-sky-700", value: "text-sky-900" },
  in_stock: { base: "border-emerald-200 bg-emerald-50", on: "border-emerald-400 ring-emerald-300", label: "text-emerald-700", value: "text-emerald-900" },
  sold: { base: "border-violet-200 bg-violet-50", on: "border-violet-400 ring-violet-300", label: "text-violet-700", value: "text-violet-900" },
  leased: { base: "border-orange-200 bg-orange-50", on: "border-orange-400 ring-orange-300", label: "text-orange-700", value: "text-orange-900" },
};

const label = (list, v) => list.find(([k]) => k === v)?.[1] || v;
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function PlatformAssets() {
  const [data, setData] = useState({ items: [], counts: null });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [status, setStatus] = useState("");
  const [dialog, setDialog] = useState(undefined); // undefined closed, null new, object edit

  const load = useCallback(async (offset = 0) => {
    offset ? setMore(true) : setLoading(true);
    try {
      const res = await api.get("/platform/assets", { params: { q: dq, status, limit: PAGE_SIZE, offset } });
      setTotal(totalFrom(res));
      setData((d) => ({ counts: res.data.counts, items: offset ? [...d.items, ...res.data.items] : res.data.items }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load devices"); }
    finally { setLoading(false); setMore(false); }
  }, [dq, status]);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.asset_number} (${a.brand} ${a.model})? This cannot be undone.`)) return;
    try { await api.delete(`/platform/assets/${a.id}`); toast.success(`${a.asset_number} deleted`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/platform/assets/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "devices.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const c = data.counts;
  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-assets-page">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><MonitorSmartphone className="h-6 w-6 text-primary" /> Devices</h1>
          <div className="text-xs text-slate-500">Kiosk tablets and hardware you sell or lease to customers. Asset numbers are assigned automatically.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="assets-export-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"><FileDown className="h-4 w-4" /> CSV</button>
          <button onClick={() => setDialog(null)} data-testid="asset-add-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:shadow transition"><Plus className="h-4 w-4" /> Register device</button>
        </div>
      </header>

      {c && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="assets-counts">
          {[["", "All devices", c.total], ...STATUSES.map(([v, l]) => [v, l, c[v]])].map(([v, l, n]) => {
            const tone = COUNT_TONE[v || "all"];
            return (
            <button key={v} onClick={() => setStatus(v)} data-testid={`assets-count-${v || "all"}`}
              className={`rounded-xl border p-4 text-left transition ${tone.base} ${status === v ? `${tone.on} ring-2 ring-inset` : "hover:brightness-[0.98]"}`}>
              <div className={`text-[11px] uppercase tracking-widest ${tone.label}`}>{l}</div>
              <div className={`font-display text-2xl font-bold mt-1 ${tone.value}`}>{n}</div>
            </button>
            );
          })}
        </div>
      )}

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="platform-assets">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search asset #, serial, brand, customer…" data-testid="assets-search"
              className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </div>
          <span className="text-xs text-slate-500 ml-auto">{total} device{total === 1 ? "" : "s"}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="assets-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Asset #</th><th className="px-5 py-3">Device</th><th className="px-5 py-3">Condition</th><th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Customer / location</th><th className="px-5 py-3">Date</th><th className="px-5 py-3 text-right">Price</th><th className="px-5 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">No devices yet — click "Register device" to add your first tablet.</td></tr>}
              {data.items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`asset-row-${a.id}`}>
                  <td className="px-5 py-3 font-mono font-semibold text-slate-900" data-testid={`asset-number-${a.id}`}>{a.asset_number}</td>
                  <td className="px-5 py-3"><div className="font-medium text-slate-900">{a.brand} {a.model}</div><div className="text-[11px] font-mono text-slate-500">SN {a.serial_number}</div></td>
                  <td className={`px-5 py-3 font-medium ${COND_TONE[a.condition] || ""}`}>{label(CONDITIONS, a.condition)}</td>
                  <td className="px-5 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_TONE[a.status]}`} data-testid={`asset-status-${a.id}`}>{label(STATUSES, a.status)}</span></td>
                  <td className="px-5 py-3">
                    {a.status === "in_stock" ? <span className="text-slate-400">Warehouse</span> : <>
                      <div className="font-medium text-slate-900">{a.customer_name}{a.company_name && a.company_name !== a.customer_name && <span className="text-slate-400 font-normal"> · {a.company_name}</span>}</div>
                      {a.location_address && <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location_address}{a.address_verified && <BadgeCheck className="h-3 w-3 text-emerald-600" />}</div>}
                    </>}
                  </td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(a.date)}{a.status === "leased" && a.lease_end && <div className="text-[11px] text-slate-500">until {fmtDate(a.lease_end)}</div>}</td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">{a.price != null ? `$${Number(a.price).toFixed(2)}${a.status === "leased" ? "/mo" : ""}` : "—"}</td>
                  <td className="px-5 py-3"><div className="flex items-center justify-end gap-0.5">
                    <IconAction onClick={() => setDialog(a)} Icon={Pencil} label="Edit" tone="primary" testid={`asset-edit-${a.id}`} />
                    <IconAction onClick={() => remove(a)} Icon={Trash2} label="Delete" tone="danger" testid={`asset-delete-${a.id}`} />
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <LoadMoreBar shown={data.items.length} total={total} loading={more} testid="assets-load-more" onMore={() => load(data.items.length)} />
      </section>

      {dialog !== undefined && <AssetDialog asset={dialog} onClose={() => setDialog(undefined)} onSaved={() => { setDialog(undefined); load(); }} />}
    </main>
  );
}
